import { signal } from '@angular/core';

import { deepEqual } from '@recruitee/utils';

import type { Select2Item } from '../../types';
import { ItemSelector } from '../../types';

export class SelectItemStrategy<Item extends Select2Item> extends ItemSelector<Item> {
  public selected = signal<Item | null>(null, { equal: deepEqual });

  private formControlValue: Item['value'];
  private hiddenItems: Item[] | null = null;
  private items: Item[] | null;

  public select(item: Item) {
    this.selected.set(item);
    this.selectionChanged$.next();
  }

  public clear() {
    this.selected.set(null);
    this.selectionChanged$.next();
  }

  public isSelected(item: Item) {
    const selected = this.selected();
    return !!selected && this.compareItemsFn(item, selected);
  }

  public getValue(): Item['value'] | null {
    return this.selected()?.value ?? null;
  }

  public setItems(items: Item[]) {
    this.items = items;
    this.updateSelection();
  }

  public setHiddenItems(items: Item[] | null) {
    this.hiddenItems = items;
    this.updateSelection();
  }

  public writeValue(value: Item['value']) {
    this.formControlValue = value;
    this.updateSelection();
  }

  public setCompareItemsFn(fn: (a: Item, b: { value: Item['value'] } | Item) => boolean) {
    this.compareItemsFn = fn;
    this.updateSelection();
  }

  private updateSelection() {
    const items = Array.isArray(this.items) ? this.items : [];
    const hiddenItems = Array.isArray(this.hiddenItems) ? this.hiddenItems : [];
    const item = [...hiddenItems, ...items].find(item =>
      this.compareItemsFn(item, { value: this.formControlValue }),
    );
    this.selected.set(item ?? null);
  }
}
